import { useCallback, useEffect, useMemo, useState } from 'react'
import { SearchX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import AmicalCaReservationModal from '../../../../amicales/components/AmicalCaReservationModal'
import NomadSearchHero from '../../shared/NomadSearchHero'
import { SoftButton } from '@/ui/design-system'
import { cartesApi, reservationsApi } from '../api'
import {
  CARD_TYPE,
  CardGridSkeleton,
  EmptyState,
  MODULE_BASE,
  SearchableSelect,
  ViewToggle,
  useCartesAccesLabels,
  useDebounce,
  useToast,
  useUrlFilters,
} from '../shared'
import ResultCard from './ResultCard'
import ResultListItem from './ResultListItem'
import ReservationModal from './ReservationModal'
import SearchFilters from './SearchFilters'

const Recherche = ({
  embed = false,
  hideTypeFilter = false,
  fixedType = null,
  partnerClientId = null,
  urlKeepKeys = [],
  isAmical = false,
}) => {
  const { t } = useTranslation()
  const { sortOptions } = useCartesAccesLabels()
  const { get, setParam, setParams, clear } = useUrlFilters()
  const { notify, ToastViewport } = useToast()
  const navigate = useNavigate()

  const filters = {
    country: get('country'),
    city: get('city'),
    date: get('date'),
    type: fixedType || get('type'),
  }
  const sort = get('sort') || 'relevance'
  const view = get('view') || 'grid'

  const heroTitle =
    fixedType === CARD_TYPE.BIEN_ETRE
      ? t('cartesAcces.common.cardType.bienEtre')
      : fixedType === CARD_TYPE.ACCES
        ? t('cartesAcces.common.cardType.acces')
        : t('cartesAcces.recherche.title')

  const heroDescription = t('cartesAcces.recherche.description')

  const heroBreadcrumbs = [
    t('cartesAcces.common.moduleName'),
    embed ? heroTitle : t('cartesAcces.recherche.breadcrumb'),
  ]

  const debounced = useDebounce(JSON.stringify({ ...filters, sort, partnerClientId }), 400)

  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(true)
  const [reserveTarget, setReserveTarget] = useState(null)

  const modalDefaults = useMemo(() => ({ date: filters.date }), [filters.date])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await cartesApi.search({
        country: filters.country || undefined,
        city: filters.city || undefined,
        date: filters.date || undefined,
        type: filters.type && filters.type !== 'all' ? filters.type : undefined,
        partnerClientId: partnerClientId || undefined,
        sort,
      })
      setResults(data)
    } catch {
      notify(t('cartesAcces.recherche.toast.searchError'), 'error')
      setResults([])
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced])

  useEffect(() => {
    load()
  }, [load])

  const handleConfirm = async (payload, { keepOpen = false } = {}) => {
    try {
      const data = await reservationsApi.create(payload)
      if (!isAmical) {
        notify(t('cartesAcces.recherche.toast.confirmed'), 'success')
        if (!keepOpen) {
          setReserveTarget(null)
          if (!embed) {
            navigate(`${MODULE_BASE}/reservations`)
          }
        }
      }
      return data
    } catch {
      notify(t('cartesAcces.recherche.toast.failed'), 'error')
      throw new Error('create_failed')
    }
  }

  const handleClearFilters = () => {
    clear(embed ? urlKeepKeys : [])
  }

  return (
    <div className={embed ? 'w-full' : 'mx-auto max-w-[1280px]'}>
      <ToastViewport />
      <NomadSearchHero
        breadcrumbs={heroBreadcrumbs}
        title={heroTitle}
        description={heroDescription}
        breadcrumbAria={t('cartesAcces.recherche.breadcrumb')}
        variant={fixedType === CARD_TYPE.BIEN_ETRE ? 'spa' : 'cartes'}
      >
        <SearchFilters
          filters={filters}
          onChange={(key, value) => setParam(key, value)}
          onChangeMany={(patch) => setParams(patch)}
          hideTypeFilter={hideTypeFilter || Boolean(fixedType)}
          embedded
        />
      </NomadSearchHero>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-[var(--foreground)]">
          {loading
            ? t('cartesAcces.recherche.loading')
            : t('cartesAcces.recherche.resultsCount', { count: results.length })}
        </h2>
        <div className="flex items-center gap-3">
          <div className="w-[200px]">
            <SearchableSelect
              options={sortOptions}
              value={sort}
              onChange={(v) => setParam('sort', v)}
              aria-label={t('cartesAcces.recherche.sortAria')}
            />
          </div>
          <ViewToggle value={view} onChange={(v) => setParam('view', v)} views={['grid', 'list']} />
        </div>
      </div>

      {loading ? (
        <CardGridSkeleton minWidth={300} />
      ) : results.length === 0 ? (
        <EmptyState
          icon={SearchX}
          title={t('cartesAcces.recherche.emptyTitle')}
          description={t('cartesAcces.recherche.emptyDescription')}
          action={
            <SoftButton variant="soft" onClick={handleClearFilters}>
              {t('cartesAcces.recherche.clearFilters')}
            </SoftButton>
          }
        />
      ) : view === 'list' ? (
        <div className="space-y-3">
          {results.map((carte) => (
            <ResultListItem key={carte.id} carte={carte} onReserve={setReserveTarget} />
          ))}
        </div>
      ) : (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
          {results.map((carte) => (
            <ResultCard key={carte.id} carte={carte} onReserve={setReserveTarget} />
          ))}
        </div>
      )}

      <ReservationModal
        carte={reserveTarget}
        open={Boolean(reserveTarget) && !isAmical}
        defaults={modalDefaults}
        onClose={() => setReserveTarget(null)}
        onConfirm={handleConfirm}
      />

      <AmicalCaReservationModal
        carte={reserveTarget}
        open={Boolean(reserveTarget) && isAmical}
        defaults={modalDefaults}
        onClose={() => setReserveTarget(null)}
        onConfirm={handleConfirm}
      />
    </div>
  )
}

export default Recherche
